import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    UserShortcutContainerColorConstantType,
    UserShortcutActionRadiusConstantType,
    UserShortcutStatusColorConstantType,
    UserShortcutStatusDotConstantType,
    UserShortcutMeasuresConstantType,
    UserShortcutRadiusConstantType,
    UserShortcutColorsConstantType,
    UserShortcutDynamicStyleType,
    UserShortcutAvatarPlacement,
    UserShortcutSpacesType,
    UserShortcutMeasures,
    UserShortcutVariant,
    UserShortcutColors
} from "./type";
import type {
    Mutable
} from "../../types";
import type {
    AvatarSize
} from "../avatar/type";
import {
    getSurfaceStyle
} from "../../helpers/theme/elevation";
import {
    getFocusRingStyle
} from "../../helpers/theme/state";
import {
    concentric
} from "../../helpers/theme/concentric";
import {
    getCornerStyle
} from "../../helpers/theme/corner";
import {
    STATUS_CHEAT_SHEET,
    webStyle
} from "../../utils";
import {
    getHitArea
} from "../../helpers/theme/control";
import {
    withAlpha
} from "../../utils/color";

export const USER_SHORTCUT_SPACES: Record<UserShortcutVariant, UserShortcutSpacesType> = {
    compact: {
        horizontal: "spacingXs",
        vertical: "spacingXs"
    },
    default: {
        horizontal: "spacingMd",
        vertical: "spacingSm"
    },
    drawer: {
        horizontal: "spacingMd",
        vertical: "spacingMd"
    }
};

export const USER_SHORTCUT_AVATAR_SIZES: Record<UserShortcutAvatarPlacement, AvatarSize> = {
    trigger: "small",
    sheet: "medium",
    menu: "xSmall"
};

export const USER_SHORTCUT_DEFAULT_STATUS_COLOR: keyof NCoreUIKit.TextContentColors = "success";

export const getUserShortcutRadius = ({
    isListSurface,
    radiuses,
    borders,
    variant,
    spaces
}: UserShortcutRadiusConstantType): number => {
    if(variant === "compact") {
        return radiuses.full;
    }

    if(variant === "drawer") {
        return radiuses.none;
    }

    if(!isListSurface) {
        return radiuses.actions;
    }

    return concentric({
        inset: spaces.spacingXs + borders.line,
        outer: radiuses.surface,
        role: radiuses.actions,
        isCapsuleExempt: false
    });
};

export const getUserShortcutMeasures = ({
    isListSurface,
    touchTargets,
    pointerType,
    avatarSize,
    radiuses,
    borders,
    variant,
    spaces
}: UserShortcutMeasuresConstantType): UserShortcutMeasures => {
    const currentSpaces = USER_SHORTCUT_SPACES[variant];

    const paddingVertical = spaces[currentSpaces.vertical];

    const hitArea = getHitArea({
        visualSize: avatarSize + paddingVertical * 2,
        touchTargets,
        pointerType
    });

    return {
        paddingHorizontal: spaces[currentSpaces.horizontal],
        borderRadius: getUserShortcutRadius({
            isListSurface,
            radiuses,
            borders,
            variant,
            spaces
        }),
        minHeight: hitArea.minHeight,
        minWidth: hitArea.minWidth,
        paddingVertical
    };
};

export const getUserShortcutActionRadius = ({
    currentMeasures,
    isListSurface,
    radiuses,
    borders,
    spaces
}: UserShortcutActionRadiusConstantType): number | undefined => {
    if(!isListSurface) {
        return undefined;
    }

    const listInset = spaces.spacingXs + borders.line;

    return concentric({
        inset: {
            horizontal: listInset + currentMeasures.paddingHorizontal,
            vertical: listInset + currentMeasures.paddingVertical
        },
        outer: radiuses.surface,
        role: radiuses.actions
    });
};

export const getUserShortcutContainerColor = ({
    backgroundColor,
    colors
}: UserShortcutContainerColorConstantType): string | undefined => {
    if(backgroundColor === "transparent") {
        return undefined;
    }

    return colors.content.container[backgroundColor];
};

export const getUserShortcutStatusColorKey = ({
    statusIndicatorType,
    statusColor
}: UserShortcutStatusColorConstantType): keyof NCoreUIKit.TextContentColors => {
    if(statusColor) {
        return statusColor;
    }

    if(!statusIndicatorType) {
        return USER_SHORTCUT_DEFAULT_STATUS_COLOR;
    }

    return STATUS_CHEAT_SHEET[statusIndicatorType].text;
};

export const getUserShortcutColors = ({
    statusColor,
    isDisabled,
    colors
}: UserShortcutColorsConstantType): UserShortcutColors => {
    if(isDisabled) {
        const disabledColor = colors.system.state.content.disabled.neutral;

        return {
            statusDotColor: disabledColor,
            subTitleColor: disabledColor,
            statusColor: disabledColor,
            titleColor: disabledColor,
            iconColor: disabledColor
        };
    }

    return {
        statusDotColor: colors.content.text[statusColor],
        iconColor: colors.content.icon.default,
        subTitleColor: colors.content.text.low,
        statusColor: colors.content.text.mid,
        titleColor: colors.content.text.high
    };
};

export const getUserShortcutStatusDotStyle = ({
    shapeConfigs,
    radiuses,
    borders,
    spaces,
    color
}: UserShortcutStatusDotConstantType): ViewStyle => {
    return {
        boxShadow: [
            {
                color: withAlpha({
                    alpha: shapeConfigs.ringAlpha,
                    color
                }),
                spreadDistance: borders.subtract,
                offsetX: 0,
                offsetY: 0
            }
        ],
        ...getCornerStyle({
            radius: radiuses.chip,
            shapeConfigs
        }),
        height: spaces.spacingSm,
        backgroundColor: color,
        width: spaces.spacingSm
    };
};

const stylesheet = StyleSheet.create({
    triggerContainer: {
        justifyContent: "center",
        alignItems: "stretch",
        ...webStyle({
            outlineStyle: "solid",
            outlineWidth: 0
        })
    },
    contentContainer: {
        justifyContent: "center",
        flexDirection: "column",
        minWidth: 0,
        flex: 1
    },
    chevronContainer: {
        justifyContent: "center",
        alignItems: "center",
        flexShrink: 0
    },
    sheetHeaderContent: {
        flexDirection: "column",
        flexShrink: 1,
        minWidth: 0
    },
    sheetContent: {
        flexDirection: "column"
    },
    container: {
        flexDirection: "column"
    },
    title: {
        alignSelf: "flex-start"
    },
    badgeContainer: {
        pointerEvents: "none",
        position: "absolute",
        bottom: 0,
        start: 0,
        end: 0,
        top: 0
    },
    compactTriggerContainer: {
        alignItems: "center"
    },
    baselineContainer: {
        alignSelf: "baseline",
        maxWidth: "100%",
        width: "auto",
        minWidth: 0
    },
    subTitleContainer: {
        flexDirection: "row",
        alignItems: "center",
        minWidth: 0
    },
    stretchContainer: {
        alignSelf: "stretch",
        width: "100%",
        flexShrink: 1
    },
    avatarContainer: {
        position: "relative",
        flexShrink: 0
    },
    sheetHeader: {
        flexDirection: "row",
        alignItems: "center"
    },
    surface: {
        flexDirection: "row",
        alignItems: "center"
    },
    statusRow: {
        flexDirection: "row",
        alignItems: "center",
        minWidth: 0
    },
    menuAnchor: {
        alignSelf: "stretch"
    },
    subTitle: {
        flexShrink: 1
    },
    status: {
        flexShrink: 1
    },
    spacer: {
        flex: 1
    }
});

export const useStyles = ({
    currentMeasures,
    spreadBehaviour,
    containerColor,
    isFocusVisible,
    isListSurface,
    shapeConfigs,
    shadowAlpha,
    elevations,
    maxWidth,
    radiuses,
    borders,
    variant,
    colors,
    spaces
}: UserShortcutDynamicStyleType) => {
    const styles = {
        surface: {
            paddingHorizontal: currentMeasures.paddingHorizontal,
            paddingVertical: currentMeasures.paddingVertical,
            ...getCornerStyle({
                radius: currentMeasures.borderRadius,
                shapeConfigs
            }),
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>,
        triggerContainer: {
            minHeight: currentMeasures.minHeight,
            minWidth: currentMeasures.minWidth
        } as Mutable<ViewStyle>,
        menuHeader: {
            padding: spaces.spacingSm,
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>,
        subTitleContainer: {
            gap: spaces.spacingXs
        } as Mutable<ViewStyle>,
        statusRow: {
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>,
        sheetContent: {
            gap: spaces.spacingMd
        } as Mutable<ViewStyle>,
        sheetHeader: {
            gap: spaces.spacingMd
        } as Mutable<ViewStyle>,
        container: {
            maxWidth
        } as Mutable<ViewStyle>
    };

    if(isListSurface) {
        Object.assign(styles.container, getSurfaceStyle({
            surface: "card",
            shadowAlpha,
            elevations,
            borders,
            colors
        }), {
            backgroundColor: containerColor,
            padding: spaces.spacingXs
        }, getCornerStyle({
            radius: radiuses.surface,
            shapeConfigs
        }));
    } else if(containerColor) {
        styles.surface.backgroundColor = containerColor;
    }

    Object.assign(styles.surface, getFocusRingStyle({
        isInset: variant === "drawer",
        isFocusVisible,
        borders,
        colors
    }));

    if(variant === "compact") {
        Object.assign(styles.triggerContainer, stylesheet.compactTriggerContainer);
    }

    if(spreadBehaviour === "baseline") {
        Object.assign(styles.container, stylesheet.baselineContainer);
    }

    if(spreadBehaviour === "stretch") {
        Object.assign(styles.container, stylesheet.stretchContainer);
    }

    return styles;
};
export default stylesheet;
